import clsx from "clsx";
import { type ComponentProps, useMemo } from "react";

import type { IconBaseType } from "@shared/content-types/types";
import { useImageMaskRef } from "@shared/hooks/use-image-mask-ref";
import { BrandIcon } from "@shared/ui/brand-icon";
import { Button } from "@shared/ui/button";
import { Icon } from "@shared/ui/icon";
import { Image } from "@shared/ui/image";
import { Body, Heading } from "@shared/ui/typography";
import { mod } from "@shared/utils";

import type { EcotrapType } from "@/_pages/ecotrap/model";

import s from "./ecotrap-card.module.scss";

export const trapTypes = {
  linear: {
    text: "Линейный",
    icon: "linear" as IconBaseType["name"],
  },
  radial: {
    text: "Радиальный",
    icon: "radial" as IconBaseType["name"],
  },
  circular: {
    text: "Круговой",
    icon: "circular" as IconBaseType["name"],
  },
};

export type EcotrapCardProps = ComponentProps<"div"> & {
  data: EcotrapType;
  index: number;
  /** Номер тропы с карты; если не передан — считаем по позиции в списке */
  number?: string;
};

export const EcotrapCard = (props: EcotrapCardProps) => {
  const { data, index, number } = props;

  const $imageRef = useImageMaskRef();

  const updatedVariant = useMemo(() => {
    return data.details.difficulty?.split(" ").join("-");
  }, [data.details.difficulty]);

  const mods = mod(s, {
    variant: updatedVariant,
  });

  return (
    <article className={clsx(s.root, mods)}>
      <Button href={`/ecotrails/${data.slug}`} className={s.wrapper}>
        <div className={s.icons}>
          <Heading level={6} tag="div" className={s.counter}>
            {number ?? index + 1}
          </Heading>
          <BrandIcon name="arrow" fillColor="white" className={s.arrowIcon} />
        </div>
        <div className={s.top}>
          <Icon
            className={s.trapIcon}
            name={trapTypes[data.details.type as keyof typeof trapTypes].icon}
          />
          <Body size="uppercase-small">
            {trapTypes[data.details.type as keyof typeof trapTypes].text}{" "}
            маршрут
          </Body>
        </div>
        <div className={s.middle} ref={$imageRef}>
          {data?.gallery?.[0]?.url && (
            <Image
              src={data.gallery[0].url}
              alt={data.gallery[0]?.alternativeText ?? "card image"}
              height="100%"
              className={s.image}
            />
          )}
        </div>
        <div className={s.bottom}>
          <Heading level={6} tag="h2" className={s.name}>
            <span>{data.title}</span>
            {data.ecotrap_type?.name && (
              <span>{`,  ${data.ecotrap_type?.name}`}</span>
            )}
          </Heading>
          <Body weight="semiBold" className={s.distance}>
            {data.details.distance_time}
          </Body>
        </div>
      </Button>
    </article>
  );
};

EcotrapCard.displayName = "EcotrapCard";
